'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  durationToMs,
  formatDuration,
  formatFullDateTime,
  msToDuration,
  parseDateTimeLocal,
  toDateTimeLocalValue,
} from '@/lib/browser-time-tools-extra';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type Mode = 'addSubtract' | 'between';

function localInstant(value: string): Date | null {
  const fields = parseDateTimeLocal(value);
  if (!fields) return null;
  return new Date(fields.year, fields.month - 1, fields.day, fields.hour, fields.minute, 0);
}

export default function BrowserTimeCalculator() {
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<Mode>('addSubtract');
  const [baseTime, setBaseTime] = useState('');
  const [operation, setOperation] = useState<'add' | 'subtract'>('add');
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  useEffect(() => {
    const now = new Date();
    const nowValue = toDateTimeLocalValue(now);
    setBaseTime(nowValue);
    setStartTime(nowValue);
    setEndTime(toDateTimeLocalValue(new Date(now.getTime() + 90 * 60000)));
    setReady(true);
  }, []);

  const addSubtractResult = useMemo(() => {
    const base = localInstant(baseTime);
    if (!base) return null;
    const deltaMs = durationToMs({
      days: Number(days) || 0,
      hours: Number(hours) || 0,
      minutes: Number(minutes) || 0,
      seconds: Number(seconds) || 0,
    });
    const signed = operation === 'subtract' ? -deltaMs : deltaMs;
    return new Date(base.getTime() + signed);
  }, [baseTime, days, hours, minutes, seconds, operation]);

  const betweenResult = useMemo(() => {
    const start = localInstant(startTime);
    const end = localInstant(endTime);
    if (!start || !end) return null;
    const diffMs = end.getTime() - start.getTime();
    return {
      negative: diffMs < 0,
      duration: msToDuration(Math.abs(diffMs)),
      totalMs: Math.abs(diffMs),
    };
  }, [startTime, endTime]);

  async function handleCopy(value: string) {
    try {
      await copyText(value);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Time calculator</span>
        <h2 className="tool-live-title">Time calculator</h2>
        <p className="tool-live-subtitle">
          Add or subtract durations, or measure the gap between two times. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Mode</span>
              <select onChange={(event) => setMode(event.target.value as Mode)} value={mode}>
                <option value="addSubtract">Add / subtract a duration</option>
                <option value="between">Duration between two times</option>
              </select>
            </label>

            {mode === 'addSubtract' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Base date and time</span>
                  <input
                    onChange={(event) => setBaseTime(event.target.value)}
                    type="datetime-local"
                    value={baseTime}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Operation</span>
                  <select
                    onChange={(event) => setOperation(event.target.value as 'add' | 'subtract')}
                    value={operation}
                  >
                    <option value="add">Add</option>
                    <option value="subtract">Subtract</option>
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Days</span>
                  <input
                    inputMode="numeric"
                    onChange={(event) => setDays(Number(event.target.value))}
                    type="number"
                    value={days}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Hours</span>
                  <input
                    inputMode="numeric"
                    onChange={(event) => setHours(Number(event.target.value))}
                    type="number"
                    value={hours}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Minutes</span>
                  <input
                    inputMode="numeric"
                    onChange={(event) => setMinutes(Number(event.target.value))}
                    type="number"
                    value={minutes}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Seconds</span>
                  <input
                    inputMode="numeric"
                    onChange={(event) => setSeconds(Number(event.target.value))}
                    type="number"
                    value={seconds}
                  />
                </label>
              </>
            ) : (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Start date and time</span>
                  <input
                    onChange={(event) => setStartTime(event.target.value)}
                    type="datetime-local"
                    value={startTime}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>End date and time</span>
                  <input
                    onChange={(event) => setEndTime(event.target.value)}
                    type="datetime-local"
                    value={endTime}
                  />
                </label>
              </>
            )}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          <p className="tool-live-panel-note">All math runs locally in your browser tab.</p>
        </section>

        <section className="tool-live-preview">
          {!ready ? (
            <p className="tool-live-panel-note">Loading…</p>
          ) : mode === 'addSubtract' ? (
            addSubtractResult ? (
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <p className="tool-live-panel-note" style={{ marginBottom: 4 }}>
                    Result
                  </p>
                  <p
                    className="tool-live-title"
                    style={{ fontSize: 24, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatFullDateTime(addSubtractResult)}
                  </p>
                </div>
                <div className="tool-live-actions">
                  <button
                    className="button button-secondary"
                    onClick={() => handleCopy(formatFullDateTime(addSubtractResult))}
                    type="button"
                  >
                    Copy result
                  </button>
                </div>
              </div>
            ) : (
              <p className="status-message" style={{ color: '#dc2626' }}>
                Enter a valid base date and time.
              </p>
            )
          ) : betweenResult ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <p className="tool-live-panel-note" style={{ marginBottom: 4 }}>
                  Duration{betweenResult.negative ? ' (end is before start)' : ''}
                </p>
                <p
                  className="tool-live-title"
                  style={{ fontSize: 24, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatDuration(betweenResult.duration)}
                </p>
              </div>
              <p className="tool-live-panel-note">
                Totals: {Math.floor(betweenResult.totalMs / 3600000)} hours ·{' '}
                {Math.floor(betweenResult.totalMs / 60000)} minutes ·{' '}
                {Math.floor(betweenResult.totalMs / 1000)} seconds
              </p>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => handleCopy(formatDuration(betweenResult.duration))}
                  type="button"
                >
                  Copy result
                </button>
              </div>
            </div>
          ) : (
            <p className="status-message" style={{ color: '#dc2626' }}>
              Enter valid start and end times.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
